// frontend/src/pages/Profile.jsx
import { useEffect, useState } from "react";
import api from "../api/api";
import { toast } from "react-toastify";
import Currency from "../components/Currency";
import {
  User, Mail, Calendar, Wallet, Lock, Eye, EyeOff,
  Pencil, Check, X, ShieldCheck, KeyRound,
} from "lucide-react";

export default function Profile() {
  const [user, setUser] = useState({
    username: "",
    email: "",
    createdAt: "",
  });
  const [walletBalance, setWalletBalance] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ username: "", email: "" });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Confirmation modals
  const [showProfileConfirm, setShowProfileConfirm] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  useEffect(() => {
    fetchProfile();
    fetchWallet();
    fetchTotalSpent();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get("/user/me");
      const data = res.data;

      setUser({
        username: data.username || "User",
        email: data.email || "Not set",
        createdAt: data.createdAt
          ? new Date(data.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "Unknown",
      });
      setFormData({
        username: data.username || "",
        email: data.email || "",
      });
    } catch (err) {
      toast.error("Failed to load profile");
    }
  };

  const fetchWallet = async () => {
    try {
      const res = await api.get("/wallet");
      setWalletBalance(Number(res.data?.balance || 0));
    } catch (err) {
      setWalletBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalSpent = async () => {
    try {
      const res = await api.get("/transactions/me");
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.transactions || res.data?.data || [];

      const spent = data.reduce((sum, t) => {
        const amt = Math.abs(Number(t.amount || 0));
        if (["debit", "purchase", "buy", "rebuy"].includes(t.type)) {
          return sum + amt;
        }
        if (t.type === "refund") {
          return sum - amt;
        }
        return sum;
      }, 0);

      setTotalSpent(spent);
    } catch (err) {
      setTotalSpent(0);
    }
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    setShowProfileConfirm(true);
  };

  const confirmSaveProfile = async () => {
    setShowProfileConfirm(false);
    setSavingProfile(true);
    toast.info("Updating profile...");
    try {
      const res = await api.put("/user/me", formData);
      toast.success(res.data.message || "Profile updated successfully");
      setEditMode(false);
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setShowPasswordConfirm(true);
  };

  const confirmChangePassword = async () => {
    setShowPasswordConfirm(false);
    setSavingPassword(true);
    toast.info("Changing password...");

    try {
      const res = await api.put("/user/change-password", {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      toast.success(res.data.message || "Password changed successfully!");
      setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to change password";
      toast.error(errorMsg);
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const initials = user.username.charAt(0).toUpperCase();

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Profile</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your account information and security settings
        </p>
      </div>

      {/* ── Identity hero card ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 dark:from-purple-700 dark:to-purple-950 p-6 sm:p-7 mb-6 shadow-lg">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -left-8 -bottom-12 w-40 h-40 rounded-full bg-white/5 blur-2xl" />

        <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/15 border-2 border-white/30 backdrop-blur-sm flex items-center justify-center text-white text-3xl sm:text-4xl font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="text-center sm:text-left flex-1 min-w-0">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
              <h2 className="text-xl sm:text-2xl font-bold text-white truncate">{user.username}</h2>
              <ShieldCheck size={18} className="text-emerald-300 flex-shrink-0" />
            </div>
            <p className="text-sm text-purple-100 truncate">{user.email}</p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
              <span className="flex items-center gap-1.5 text-xs font-medium text-white bg-white/15 px-3 py-1.5 rounded-full">
                <Calendar size={12} />
                Joined {user.createdAt}
              </span>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 px-3 py-1.5 rounded-full">
                <Wallet size={12} />
                <Currency amount={walletBalance} />
              </span>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 px-3 py-1.5 rounded-full">
                <Currency amount={totalSpent} /> spent
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Account Information ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Account information</h2>
          {!editMode && (
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="flex items-center gap-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition"
            >
              <Pencil size={14} />
              Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSaveProfile}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <User size={13} /> Username
              </label>
              {editMode ? (
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900 dark:text-white px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                  {user.username}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Mail size={13} /> Email
              </label>
              {editMode ? (
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900 dark:text-white px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-xl truncate">
                  {user.email}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Calendar size={13} /> Joined
              </label>
              <p className="text-sm font-medium text-gray-900 dark:text-white px-3.5 py-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                {user.createdAt}
              </p>
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                <Wallet size={13} /> Wallet balance
              </label>
              <p className="text-sm font-bold text-purple-700 dark:text-purple-400 px-3.5 py-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                <Currency amount={walletBalance} />
              </p>
            </div>
          </div>

          {editMode && (
            <div className="mt-5 flex gap-3">
              <button
                type="submit"
                disabled={savingProfile}
                className={`flex items-center gap-1.5 bg-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-purple-700 transition ${
                  savingProfile ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Check size={15} />
                {savingProfile ? "Saving..." : "Save changes"}
              </button>
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                <X size={15} />
                Cancel
              </button>
            </div>
          )}
        </form>
      </div>

      {/* ── Change Password ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
            <KeyRound size={15} className="text-purple-600 dark:text-purple-300" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Change password</h2>
        </div>

        <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              Current password
            </label>
            <div className="relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showOld ? "text" : "password"}
                value={passwordData.oldPassword}
                onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
                required
              />
              <button
                type="button"
                onClick={() => setShowOld((v) => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showOld ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              New password
            </label>
            <div className="relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showNew ? "text" : "password"}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
                required
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              Confirm new password
            </label>
            <div className="relative">
              <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showConfirm ? "text" : "password"}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingPassword}
            className={`w-full sm:w-auto flex items-center justify-center gap-1.5 bg-purple-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-purple-700 transition ${
              savingPassword ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <KeyRound size={15} />
            {savingPassword ? "Updating..." : "Update password"}
          </button>
        </form>
      </div>

      {/* ── Confirmation Modal - Profile ── */}
      {showProfileConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center mx-auto mb-4">
              <Pencil size={20} className="text-purple-600 dark:text-purple-300" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Confirm profile update</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Save these changes to your account?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowProfileConfirm(false)}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmSaveProfile}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition"
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation Modal - Password ── */}
      {showPasswordConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center mx-auto mb-4">
              <KeyRound size={20} className="text-purple-600 dark:text-purple-300" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Confirm password change</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              This cannot be undone. Continue?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPasswordConfirm(false)}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmChangePassword}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition"
              >
                Change password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}